import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import sanfordApi, { SFDatesResponse } from 'lib/sanford-api';
import classnames from 'utils/classnames';
import { FillButton, OutlineButton } from 'components/Button';
import HorizontalLine from 'components/ui/HorizontalLine';
import { SubscriptionActions } from 'lib/constants';
import { SFSubscription } from 'pages/api/account/subscriptions';
import { ModalProps } from './index';
import CheckboxIcon from 'components/icons/CheckboxIcon';
import { ManageSubscriptionProps } from './subscriptionItem';

type SubscriptionDateFrequencyModalProps = {
  action: SubscriptionActions.ACTIVATE_SUBSCRIPTION | SubscriptionActions.CHANGE_DATE_FREQUENCY;
  subscription: SFSubscription;
  handleModal: (args: ModalProps) => void;
  handleManageSubscription: (args: ManageSubscriptionProps) => void;
};

const SubscriptionDateFrequencyModal: React.FC<SubscriptionDateFrequencyModalProps> = (props) => {
  const { action, subscription, handleModal, handleManageSubscription } = props;
  const [newDate, setNewDate] = useState<SFDatesResponse['dates'][0]>(null);
  const [newFrequency, setNewFrequency] = useState<string>(null);
  const datesRef = useRef([]);

  const datesQuery = useQuery(['subscriptionDates', subscription?.id?.toString()], async () =>
    sanfordApi.getDates(subscription?.id?.toString())
  );

  const response = datesQuery?.data?.data?.result;

  useEffect(() => {
    if (response?.selectedDate) {
      setNewDate({
        date: response.selectedDate,
        description: response.dates.find((date) => date.date === response.selectedDate).description,
      });
    }
    if (response?.selectedFrequency) {
      setNewFrequency(response.selectedFrequency);
    }

    response?.dates.map(({ date }, key) => {
      if (date === response.selectedDate) {
        datesRef.current[key].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, [response]);

  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleModal({
          id: subscription?.id,
          type: action,
          show: false,
        });
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  if (datesQuery.isLoading) return <>loading...</>;

  return (
    <div className={'h-4/5 md:h-auto'}>
      <div className="mx-auto flex h-full flex-col items-center justify-start space-y-2 overflow-hidden rounded-lg bg-white p-6 drop-shadow-sm md:min-h-[650px] md:min-w-[500px]">
        <div className="flex h-full w-full flex-col space-y-2">
          <div>
            <p className={'text-center text-xl'}>{`${
              action === SubscriptionActions.ACTIVATE_SUBSCRIPTION ? 'Resume' : 'Change'
            } Subscription`}</p>
          </div>
          <div className={'py-2'}>
            <HorizontalLine />
          </div>
          <div className={'flex-1'}>
            <div className={'flex h-full flex-col'}>
              <div className="flex h-full flex-col space-y-2 px-4">
                <p className={'!-mb-2'}>Subscription Frequency:</p>
                <span className={'text-xs text-sf-blue-700 text-opacity-60'}>
                  Select your delivery frequency
                </span>
                <div className={'relative py-0'}>
                  <div className="inline-flex w-full items-start justify-start space-x-4">
                    {response?.frequency.map((freq, index) => (
                      <div
                        key={index}
                        className="flex flex-1 items-center justify-center"
                        onClick={() => setNewFrequency(freq)}
                      >
                        {newFrequency === freq && (
                          <div className="flex flex-1 items-center justify-center">
                            <FillButton buttonSize="small" fullWidth={true}>
                              <span className="normal-case">{freq}</span>
                            </FillButton>
                          </div>
                        )}
                        {newFrequency !== freq && (
                          <OutlineButton buttonSize="small" fullWidth={true}>
                            <span className="normal-case">{freq}</span>
                          </OutlineButton>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className={'py-2'}>
                  <HorizontalLine />
                </div>
                <p className={'-mb-2'}>Delivery date:</p>
                <span className={'text-xs text-sf-blue-700 text-opacity-60'}>
                  Select the delivery date
                </span>
                <div className={'relative h-full w-full overflow-hidden py-2'}>
                  <div className={'absolute h-full w-full space-y-4 overflow-y-auto'}>
                    {response?.dates.map(({ date, description }, key) => (
                      <div
                        key={date}
                        ref={(el) => (datesRef.current[key] = el)}
                        className={`flex w-full cursor-pointer items-center justify-center space-x-4 rounded-lg p-2 ${classnames(
                          {
                            'bg-sf-blue-700 bg-opacity-[4%]': date === newDate?.date,
                          }
                        )}`}
                        onClick={() => setNewDate({ date: date, description: description })}
                      >
                        <div className="flex h-6 w-6 items-center justify-center rounded border-2 border-sf-blue-700 p-0.5">
                          {date === newDate?.date && <CheckboxIcon className={'stroke-2'} />}
                        </div>
                        <div className="inline-flex h-full flex-1 flex-col items-start justify-center">
                          <div className="flex w-full flex-col items-start justify-start space-y-2">
                            <p className="w-full text-base font-medium leading-tight text-gray-800">
                              {description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={'py-2'}>
                  <HorizontalLine />
                </div>
                <p className={' text-sm'}>
                  Selected date: <span className={'font-bold'}>{newDate?.description}</span>
                </p>
                <p className={'text-sm'}>
                  Selected frequency: <span className={'font-bold'}>{newFrequency}</span>
                </p>
                <div className="inline-flex w-full items-start justify-start space-x-4">
                  <div className="flex flex-1 items-center justify-center">
                    <OutlineButton
                      buttonSize="small"
                      fullWidth={true}
                      onClick={() =>
                        handleModal({
                          id: subscription?.id,
                          type: action,
                          show: false,
                        })
                      }
                    >
                      <span className="normal-case">Cancel</span>
                    </OutlineButton>
                  </div>
                  <div className="flex flex-1 items-center justify-center">
                    <FillButton
                      buttonSize="small"
                      fullWidth={true}
                      onClick={() =>
                        handleManageSubscription({
                          action: action,
                          manageSubscription: subscription,
                          deliveryDate: newDate.date,
                          subscriptionFrequency: newFrequency,
                        })
                      }
                    >
                      <span className="normal-case">{`${
                        action === SubscriptionActions.ACTIVATE_SUBSCRIPTION ? 'Confirm' : 'Update'
                      }`}</span>
                    </FillButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDateFrequencyModal;
